import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, ImagePlus } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { getMe } from "../lib/api";

/**
 * /welcome — Internal admin-only landing.
 *
 * This screen exposes developer-facing Foundation + Pipeline links and is
 * therefore gated to admins. Non-admin users are redirected to their normal
 * home (traveler, vendor, or business) so they never see the dev screen.
 */
export default function Welcome() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [user, setUser] = useState(null);

  useEffect(() => {
    (async () => {
      const me = await getMe();
      if (!me) {
        navigate("/", { replace: true });
        return;
      }
      // Gate: admins only. Everyone else lands on their role-specific home.
      if (me.role !== "admin") {
        const home = me.role === "vendor" ? "/vendor"
                   : me.role === "business" ? "/business"
                   : "/";
        navigate(home, { replace: true });
        return;
      }
      setUser(me);
    })();
  }, [navigate]);

  if (!user) return null;

  const firstName = (user.display_name || "").split(" ")[0] || (user.email || "").split("@")[0] || "";
  const greeting = lang === "es"
    ? (firstName ? `Bienvenido, ${firstName}` : "Bienvenido")
    : (firstName ? `Welcome, ${firstName}` : "Welcome");

  return (
    <main data-testid="welcome-page" className="pt-28 pb-24 px-5 sm:px-8 max-w-5xl mx-auto">
      <div className="eyebrow">{t("welcome")}</div>
      <h1 className="font-display text-4xl sm:text-6xl mt-3 text-foreground">
        {greeting}
      </h1>

      <div className="rule mt-12 mb-10" />

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-border p-6 bg-surface">
          <div className="eyebrow mb-3">Admin tools</div>
          <p className="text-sm text-muted-foreground mb-5">
            Foundation status &amp; image pipeline are internal QA surfaces.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/foundation"
              data-testid="goto-foundation"
              className="inline-flex items-center gap-2 px-5 py-3 bg-foreground text-background hover:bg-primary transition-colors"
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
              {t("foundation_title")} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/pipeline"
              data-testid="goto-pipeline"
              className="inline-flex items-center gap-2 px-5 py-3 border border-foreground hover:bg-foreground hover:text-background transition-colors"
            >
              <ImagePlus className="w-4 h-4" strokeWidth={1.5} />
              {t("upload_title")}
            </Link>
          </div>
        </div>
        <div className="border border-border p-6 bg-surface">
          <div className="eyebrow mb-3">Admin console</div>
          <Link
            to="/admin"
            data-testid="goto-admin"
            className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-background hover:opacity-90 transition-opacity"
          >
            Open dashboard <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </main>
  );
}
